/**
 * useProductLookup — los productos que una pantalla de líneas necesita conocer,
 * sin descargar el catálogo. Ver ProductAutocomplete.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { useDebounced } from '@/hooks/useDebounced'
import { salesKeys, type ProductListItem, type ProductListResponse } from './useSales'

interface LineaConProducto {
  product_id?: string | null
  sku?: string | null
}

/**
 * Los productos que una pantalla de líneas necesita conocer: los de las líneas
 * ya cargadas (se piden por SKU o id exactos) más los elegidos en esta sesión.
 * Devuelve un array para que el código existente siga usando `products.find`.
 */
export function useProductLookup(lines: LineaConProducto[] | undefined) {
  const [recordados, setRecordados] = useState<Map<string, ProductListItem>>(new Map())

  const refs = useMemo(() => {
    const skus = new Set<string>()
    const ids = new Set<string>()
    for (const l of lines ?? []) {
      if (l?.product_id) ids.add(String(l.product_id))
      else if (l?.sku) skus.add(String(l.sku))
    }
    return { skus: [...skus].sort(), ids: [...ids].sort() }
  }, [lines])

  // Escribir un SKU a mano cambia la línea en cada tecla: sin esta espera se
  // pediría un SKU exacto por tecla. Y lo ya consultado no se vuelve a pedir,
  // aunque no haya existido (texto libre que no es un producto).
  const refsEstables = useDebounced(refs, 400)
  const [consultados, setConsultados] = useState<Set<string>>(new Set())

  // Solo se piden los que todavía no se conocen ni se consultaron.
  const faltan = useMemo(() => {
    const conocidosId = new Set([...recordados.values()].map(p => p.id))
    const conocidosSku = new Set([...recordados.values()].map(p => p.sku))
    return {
      skus: refsEstables.skus.filter(s => !conocidosSku.has(s) && !consultados.has(`sku:${s}`)),
      ids: refsEstables.ids.filter(i => !conocidosId.has(i) && !consultados.has(`id:${i}`)),
    }
  }, [refsEstables, recordados, consultados])

  const params = {
    active: 'all',
    skus: faltan.skus.join(',') || undefined,
    ids: faltan.ids.join(',') || undefined,
    page_size: Math.max(faltan.skus.length + faltan.ids.length, 1),
  }
  // ⚠️ Los productos se guardan DENTRO de la consulta y no en un efecto sobre
  // `data`: marcar lo consultado cambia `faltan`, o sea la clave de la consulta,
  // y con la clave nueva `data` vuelve vacío antes de que un efecto lo lea.
  useQuery<ProductListResponse>({
    queryKey: salesKeys.products(params),
    queryFn: async () => {
      const res: ProductListResponse = (await apiClient.get('/sales/products/', { params })).data
      setRecordados(prev => {
        const m = new Map(prev)
        for (const p of res.products ?? []) m.set(p.id, p)
        return m
      })
      setConsultados(prev => {
        const s = new Set(prev)
        for (const sku of faltan.skus) s.add(`sku:${sku}`)
        for (const id of faltan.ids) s.add(`id:${id}`)
        return s
      })
      return res
    },
    enabled: faltan.skus.length + faltan.ids.length > 0,
    staleTime: 60_000,
  })

  const remember = (product: ProductListItem) => {
    setRecordados(prev => {
      if (prev.has(product.id)) return prev
      const m = new Map(prev)
      m.set(product.id, product)
      return m
    })
  }

  const products = useMemo(() => [...recordados.values()], [recordados])
  return { products, remember }
}
